/** Public site chrome — nav bar + footer shared across marketing pages. */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export function PublicNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <nav className="sticky top-0 z-40 backdrop-blur-lg bg-background/85 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-home">
          <div className="w-9 h-9 rounded-lg bg-gradient-teal flex items-center justify-center shadow-teal-glow group-hover:scale-105 transition-transform">
            <ShieldCheck className="w-5 h-5 text-primary-foreground" />
          </div>
          <div className="font-display font-semibold text-lg tracking-tight">Treasurescare</div>
        </Link>
        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="px-3 py-1.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/app" className="text-sm font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity flex items-center gap-1.5" data-testid="nav-dashboard">
                <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="nav-signout"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-signin">Sign in</Link>
              <Link to="/signup" className="text-sm font-medium px-4 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity" data-testid="nav-signup">
                Get started
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-border py-10 mt-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
        <div className="col-span-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-teal flex items-center justify-center shadow-teal-glow">
              <ShieldCheck className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-display font-semibold">Treasurescare</span>
          </div>
          <p className="mt-3 text-muted-foreground max-w-sm">
            Device protection reimagined — AI-powered claims, human-quality repairs. Made in the UK.
          </p>
          <div className="mt-4 text-[11px] overline text-muted-foreground">© SRAM & MRAM LIMITED · UK</div>
        </div>
        <div>
          <div className="font-medium mb-2">Company</div>
          <div className="space-y-1 text-muted-foreground">
            <div><Link to="/about" className="hover:text-foreground">About</Link></div>
            <div><Link to="/blog" className="hover:text-foreground">Blog</Link></div>
            <div><Link to="/news" className="hover:text-foreground">News</Link></div>
            <div><Link to="/contact" className="hover:text-foreground">Contact</Link></div>
          </div>
        </div>
        <div>
          <div className="font-medium mb-2">Support</div>
          <div className="space-y-1 text-muted-foreground">
            <div><a href="mailto:info@treasurescare.com" className="hover:text-foreground">info@treasurescare.com</a></div>
            <div><Link to="/login" className="hover:text-foreground">Sign in</Link></div>
            <div><Link to="/signup" className="hover:text-foreground">Create account</Link></div>
            <div><Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy policy</Link></div>
            <div><Link to="/terms" className="hover:text-foreground" data-testid="footer-terms">Terms &amp; conditions</Link></div>
            <div><Link to="/delete-account" className="hover:text-rose-500" data-testid="footer-delete-account">Delete account</Link></div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicNav />
      <main>{children}</main>
      <PublicFooter />
    </div>
  );
}
